import { useEffect, useState } from 'react';
import { bnpConnectionApi, type BnpImportHistory, type BnpPreview } from './bnp-connection-api';
import { PrototypeIcon } from './PrototypeIcon';
import '../styles/modules-bnp-connection.css';

interface Props {
  readonly csrfToken: string;
  readonly onBack: () => void;
}

/** Le parcours BNP est indépendant des huit familles d'export et de la session Freebe. */
export function BnpConnectionPanel({ csrfToken, onBack }: Props): React.JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BnpPreview | null>(null);
  const [history, setHistory] = useState<readonly BnpImportHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageSucceeded, setMessageSucceeded] = useState(false);

  useEffect(() => {
    void bnpConnectionApi
      .history()
      .then(setHistory)
      .catch(() => {
        setMessageSucceeded(false);
        setMessage('Historique BNP indisponible.');
      })
      .finally(() => setHistoryLoading(false));
  }, []);

  async function inspect(selected: File | null): Promise<void> {
    setFile(selected);
    setPreview(null);
    setConfirmed(false);
    setMessage(null);
    setMessageSucceeded(false);
    if (selected === null) return;
    const kind = /\.xls$/iu.test(selected.name)
      ? 'XLS'
      : /\.pdf$/iu.test(selected.name)
        ? 'PDF'
        : /\.zip$/iu.test(selected.name)
          ? 'ZIP'
          : null;
    if (kind === null) {
      setMessage('Choisissez un export BNP .xls, un relevé .pdf ou une archive .zip.');
      return;
    }
    setBusy(true);
    try {
      setPreview(await bnpConnectionApi.preview(selected, kind, csrfToken));
    } catch {
      setMessage(
        kind === 'ZIP'
          ? 'Archive BNP refusée. Elle doit contenir un seul export .xls et 1 à 20 relevés PDF du compte enregistré, directement ou dans un unique dossier.'
          : 'Ce fichier ne correspond pas au format BNP attendu ou à l’unique compte enregistré.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function importFile(): Promise<void> {
    if (
      file === null ||
      preview === null ||
      busy ||
      (preview.requiresAccountConfirmation && !confirmed)
    )
      return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await bnpConnectionApi.import(file, preview, csrfToken, confirmed);
      setMessageSucceeded(true);
      setMessage(
        result.imported
          ? result.filesImported === undefined
            ? 'Source BNP importée. Les montants restent à rapprocher.'
            : `${result.filesImported} fichier(s) importé(s), ${result.filesAlreadyPresent} déjà présent(s). Les montants restent à rapprocher.`
          : 'Ce fichier était déjà présent ; aucune ligne dupliquée.',
      );
      setHistory(await bnpConnectionApi.history());
      setFile(null);
      setPreview(null);
      setConfirmed(false);
    } catch {
      setMessageSucceeded(false);
      setMessage('Import BNP refusé. Vérifiez le compte et réessayez.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-content bnp-connection">
      <button type="button" className="bnp-connection__back" onClick={onBack}>
        <PrototypeIcon name="arrow-left" /> Connexions
      </button>
      <header className="connections-hub__heading">
        <p className="dashboard-heading__eyebrow">Connexion bancaire distincte</p>
        <h1>BNP Paribas · compte professionnel</h1>
        <p>
          Importez l’export des opérations .xls et les relevés PDF de ce seul compte, séparément ou
          dans un ZIP. Le solde de l’export est daté ; les relevés restent à contrôler et aucun
          chiffre n’est automatiquement confirmé.
        </p>
      </header>
      <section className="bnp-connection__card" aria-labelledby="bnp-import-title">
        <div className="bnp-connection__card-header">
          <span className="bnp-connection__icon">
            <PrototypeIcon name="cloud-upload" />
          </span>
          <div>
            <p className="dashboard-panel__eyebrow">Données bancaires</p>
            <h2 id="bnp-import-title">Importer une source bancaire</h2>
            <p>Opérations .xls, relevé .pdf ou ZIP contenant un XLS et plusieurs PDF.</p>
          </div>
        </div>
        <label
          className="bnp-connection__drop"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            void inspect(event.dataTransfer.files[0] ?? null);
          }}
        >
          <PrototypeIcon name="file-text" />
          <strong>Glissez un fichier ici</strong>
          <span>ou choisissez un export .xls, un relevé .pdf ou un ZIP complet</span>
          <span className="bnp-connection__choose">Choisir un fichier</span>
          <input
            type="file"
            aria-label="Choisir un export BNP .xls, un relevé .pdf ou un ZIP"
            accept=".xls,.pdf,.zip,application/vnd.ms-excel,application/pdf,application/zip"
            onChange={(event) => {
              void inspect(event.target.files?.[0] ?? null);
              event.target.value = '';
            }}
          />
        </label>
        {busy ? (
          <p className="bnp-connection__notice" role="status">
            Contrôle en cours…
          </p>
        ) : null}
        {preview ? (
          <div className="bnp-connection__preview">
            <strong>
              {preview.kind === 'XLS'
                ? 'Export d’opérations'
                : preview.kind === 'ZIP'
                  ? 'Archive BNP complète'
                  : 'Relevé PDF privé'}
            </strong>
            <p>
              {preview.accountLabel} ·{' '}
              {preview.kind === 'PDF'
                ? 'justificatif privé, lignes non extraites'
                : `${preview.rowCount} opération(s) extraites`}
            </p>
            {preview.files ? (
              <ul className="bnp-connection__archive-files" aria-label="Fichiers de l’archive">
                {preview.files.map((entry) => (
                  <li key={entry.name}>
                    <PrototypeIcon name={entry.kind === 'XLS' ? 'coins' : 'file-text'} />
                    <span>{entry.name}</span>
                    <small>
                      {entry.kind === 'XLS' ? `${entry.rowCount} opérations` : 'PDF privé'}
                    </small>
                  </li>
                ))}
              </ul>
            ) : null}
            {preview.firstDate && preview.lastDate ? (
              <p>
                Période des opérations : {preview.firstDate} → {preview.lastDate}
              </p>
            ) : null}
            {preview.balanceDate ? (
              <p>Solde source daté du {preview.balanceDate} ; il ne vaut pas clôture du mois.</p>
            ) : null}
            {preview.kind === 'PDF' ? (
              <p>Pièce conservée en privé, contenu à vérifier avant rapprochement.</p>
            ) : null}
            {preview.requiresAccountConfirmation ? (
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />{' '}
                Je confirme qu’il s’agit de mon unique compte professionnel BNP.
              </label>
            ) : null}
            <button
              className="bnp-connection__confirm"
              type="button"
              disabled={busy || (preview.requiresAccountConfirmation && !confirmed)}
              onClick={() => void importFile()}
            >
              Confirmer l’import
            </button>
          </div>
        ) : null}
        {message ? (
          <p
            className={
              'bnp-connection__notice tosho-feedback ' +
              (messageSucceeded ? 'is-success' : 'is-error')
            }
            role="status"
          >
            {message}
          </p>
        ) : null}
      </section>
      <section className="bnp-connection__card" aria-labelledby="bnp-history-title">
        <div className="bnp-connection__card-header">
          <span className="bnp-connection__icon">
            <PrototypeIcon name="history" />
          </span>
          <div>
            <p className="dashboard-panel__eyebrow">Traçabilité</p>
            <h2 id="bnp-history-title">Historique bancaire</h2>
            <p>Sources reçues et solde daté, en attente de rapprochement.</p>
          </div>
        </div>
        {historyLoading ? (
          <p className="bnp-connection__loading" role="status">
            <span className="bnp-connection__spinner" /> Chargement de l’historique…
          </p>
        ) : history.length === 0 ? (
          <p className="bnp-connection__empty">Aucun fichier BNP importé.</p>
        ) : (
          <ul>
            {history.map((entry) => (
              <li key={entry.id}>
                <span className="bnp-connection__history-icon">
                  <PrototypeIcon name={entry.kind === 'XLS' ? 'coins' : 'file-text'} />
                </span>
                <span className="bnp-connection__history-detail">
                  <strong>{entry.kind === 'XLS' ? 'Opérations .xls' : 'Relevé PDF'}</strong>
                  <small>
                    {new Date(entry.importedAt).toLocaleDateString('fr-FR')} ·{' '}
                    {entry.kind === 'PDF'
                      ? 'Justificatif privé · lignes non extraites'
                      : `${entry.rowCount} opération(s) extraites`}
                    {entry.balanceDate ? ` · Solde au ${entry.balanceDate}` : ''}
                    {entry.imported ? '' : ' · Réimport sans doublon'}
                  </small>
                </span>
                {entry.kind === 'PDF' ? (
                  <a
                    className="bnp-connection__open"
                    href={`/api/integrations/bnp/documents/${entry.sourceImportId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <PrototypeIcon name="eye" /> Ouvrir
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
